import React, { useState, useCallback } from 'react';
import { useQuery } from '@apollo/client';
import { useLocation, useParams } from 'react-router-dom';
import { Typography } from 'antd';
import moment from 'moment';
// Api
import { GET_AGENCY_CREDIT_USAGE } from 'api/campaignV2/queries';
// Helpers
import { createSocialProfileUrlFromHandle } from 'helpers/createSocialProfileUrlFromHandle';
// Types
import {
  GetAgencyCreditUsage,
  GetAgencyCreditUsage_getAgencyCreditUsage_entities,
  GetAgencyCreditUsageVariables,
} from 'api/campaignV2/types/GetAgencyCreditUsage';
import { SortedInfo, TableFilter } from 'ui/Table';
// Constants
import { onlyDateTimeFormat } from 'constants/global';
import { DEFAULT_CURRENT_PAGE } from 'ui/Table';
// Components
import Table from 'ui/Table';

const { Title } = Typography;

const CreditUsageDetails: React.FC = () => {
  const location = useLocation();
  const { agencyId } = useParams<{ agencyId: string }>();

  const [pageSize, setPageSize] = useState<number>(10);
  const [currentPage, setCurrentPage] = useState<number>(DEFAULT_CURRENT_PAGE);

  const handleSetPagination = useCallback(
    (page: number, size: number): void => {
      setCurrentPage(page);
      setPageSize(size);
    },
    []
  );

  const [searchFilters, setSearchFilters] = useState<TableFilter[]>([]);
  const [sort, setSortInfo] = useState<SortedInfo<string>>({
    order: 'DESC' as any,
    key: 'createdAt',
  });

  const { data, loading, error } = useQuery<
    GetAgencyCreditUsage,
    GetAgencyCreditUsageVariables
  >(GET_AGENCY_CREDIT_USAGE, {
    variables: {
      input: {
        agencyId,
        limit: pageSize,
        offset: (currentPage > 1 ? currentPage - 1 : 0) * pageSize,
      },
    },
    fetchPolicy: 'cache-and-network',
    skip: !agencyId,
  });

  const locationState = location.state as
    | { agencyName?: string; agencyEmail?: string }
    | undefined;
  const agencyName = locationState?.agencyName || '';
  const agencyEmail = locationState?.agencyEmail || '';

  const columns = [
    {
      title: 'Date of Usage',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 200,
      render: (value: string) => moment(value).format(onlyDateTimeFormat),
    },
    {
      title: 'Platform',
      dataIndex: 'platform',
      key: 'platform',
      render: (value: string) => {
        return `${value.charAt(0).toUpperCase()}${value.slice(1)}`;
      },
      width: 200,
    },
    {
      title: 'Profile Used On',
      dataIndex: 'handle',
      key: 'handle',
      render: (
        value: string,
        record: GetAgencyCreditUsage_getAgencyCreditUsage_entities
      ) => {
        return (
          <a
            href={createSocialProfileUrlFromHandle(value, record.platform)}
            target="_blank"
            rel="noopener noreferrer"
          >
            {value}
          </a>
        );
      },
      width: 300,
    },
  ];

  if (error) {
    return <div>Error loading credit usage details: {error.message}</div>;
  }

  return (
    <div>
      <Title level={2}>
        Credit Usage for {agencyName || agencyId}
        {agencyEmail && ` - ${agencyEmail}`}
      </Title>

      <Table<GetAgencyCreditUsage_getAgencyCreditUsage_entities, string>
        columns={columns}
        data={data?.getAgencyCreditUsage.entities || []}
        scroll={{ x: 700 }}
        loading={loading}
        total={data?.getAgencyCreditUsage.total}
        defaultPageSize={pageSize}
        defaultCurrent={currentPage}
        setPageAndSize={handleSetPagination}
        searchFilters={searchFilters}
        setSearchFilters={setSearchFilters}
        sortInfo={sort}
        setSortInfo={setSortInfo}
      />
    </div>
  );
};

export default CreditUsageDetails;
